import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { usePredictionStore } from 'stores/predictionStore';
import { useSelectedMoviesStore } from 'stores/selectedMoviesStore';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import PredictionsTable from '../components/PredictionsTable';
import PersonMultiSelectWithTooltips from '../components/PersonMultiSelectWithTooltips';
import GenreMultiSelect from '../components/GenreMultiSelect';
import { TableRow, ColumnConfig } from '../types';
import { routes } from 'routes/routes';
import { LOAD_TABLE_FIELDS } from '../constants/table_fields';
import {
  predictionEngineSave,
  predictionEnginePredict,
} from 'api/prediction-engine';
import {
  hasPendingPrediction,
  takePendingPrediction,
} from '../utils/pendingPrediction';
import { Loader2 } from 'lucide-react';
import { handleExport } from '../utils/exportUtils';
import toast from 'react-hot-toast';
import { useHasPermissions, useIsSuperbUser } from 'hooks/useUser';
import { PERMISSIONS } from 'types/user';
import { asPrice } from 'utils/money';
import { usePredictionsEngineContext } from '../context/PredictionsEngineContext';

const ActorMultiSelect = React.memo((props: any) => (
  <PersonMultiSelectWithTooltips {...props} roleType="actor" />
));
const DirectorMultiSelect = React.memo((props: any) => (
  <PersonMultiSelectWithTooltips {...props} roleType="director" />
));
const WriterMultiSelect = React.memo((props: any) => (
  <PersonMultiSelectWithTooltips {...props} roleType="writer" />
));
const ProducerMultiSelect = React.memo((props: any) => (
  <PersonMultiSelectWithTooltips {...props} roleType="producer" />
));
const StarMultiSelect = React.memo((props: any) => (
  <PersonMultiSelectWithTooltips {...props} roleType="star" />
));

const PredictRoute: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { predictionResult, isLoading, error, savePrediction } =
    usePredictionStore();

  // A prediction handed over by the load tab. Mobile opens this tab up front,
  // because a popup cannot be opened once the request has resolved, so the
  // waiting happens here — in the foreground tab, where the browser will not
  // throttle the request — and the load tab keeps its entered rows.
  const pendingRequestId = new URLSearchParams(location.search).get('pending');
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [pendingModelId, setPendingModelId] = useState<string | null>(null);
  const [isRunningPending, setIsRunningPending] = useState<boolean>(() =>
    hasPendingPrediction(pendingRequestId),
  );
  const pendingStartedRef = useRef(false);

  useEffect(() => {
    // A ref, not the effect deps: StrictMode runs effects twice in development
    // and the request must only be taken from storage once.
    if (pendingStartedRef.current) return;
    pendingStartedRef.current = true;

    const pending = takePendingPrediction(pendingRequestId);

    if (!pending) {
      setIsRunningPending(false);
      return;
    }

    setIsRunningPending(true);

    predictionEnginePredict({
      movies: pending.movies,
      model_id: pending.model_id,
    })
      .then((response) => {
        savePrediction({
          ...response,
          movies: response.movies?.map((movie: any, index: number) => ({
            ...movie,
            is_export_allowed: pending.export_flags?.[index] ?? false,
          })),
        });
        setPendingModelId(response.model_id || pending.model_id);
      })
      .catch((err: any) => {
        setPendingError(
          err?.error || err?.message || 'Failed to run predictions.',
        );
      })
      .finally(() => setIsRunningPending(false));
  }, [pendingRequestId, savePrediction]);

  const {
    selectedMovieIds,
    toggleMovieSelection,
    selectAllMovies,
    clearSelection,
    isMovieSelected,
    getSelectedCount,
  } = useSelectedMoviesStore();
  const { selectedModel, availableModels, setSelectedModel } =
    usePredictionsEngineContext();

  // This tab mounts before the handed-over prediction exists, so the context
  // seeds selectedModel from whatever result was stored previously. Re-point it
  // at the model this prediction actually ran with, or the gross column ends up
  // labelled for the wrong one.
  useEffect(() => {
    if (!pendingModelId || selectedModel?.id === pendingModelId) return;

    const model = availableModels.find((m) => m.id === pendingModelId);

    if (model) {
      setSelectedModel(model);
    }
  }, [pendingModelId, availableModels, selectedModel, setSelectedModel]);

  const isSuperbUser = useIsSuperbUser();
  const hasSavePredictions = useHasPermissions([
    PERMISSIONS.CAN_SAVE_PREDICTIONS,
  ]);
  const hasCanSeeDetailedPredictions = useHasPermissions([
    PERMISSIONS.CAN_SEE_DETAILED_PREDICTIONS,
  ]);

  const columns: ColumnConfig[] = [
    {
      key: 'save_checkbox',
      header: '',
      headerComponent: () => (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-full tw-py-2">
          <Checkbox
            checked={someSelected ? 'indeterminate' : allSelectableSelected}
            onCheckedChange={handleSelectAll}
            disabled={selectableMovieIndices.length === 0}
          />
        </div>
      ),
      cell: {
        type: 'custom',
        component: (props) => (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-full tw-py-2">
            <Checkbox
              checked={isMovieSelected(props.rowIndex.toString())}
              onCheckedChange={() =>
                toggleMovieSelection(props.rowIndex.toString())
              }
              disabled={props.row[LOAD_TABLE_FIELDS.is_save_allowed] === false}
            />
          </div>
        ),
      },
      width: 60,
      minWidth: 60,
      maxWidth: 60,
    },
    {
      key: LOAD_TABLE_FIELDS.internal_id,
      header: 'Internal ID',
      cell: { type: 'readonly' },
      width: 370,
      resizable: true,
    },
    {
      key: LOAD_TABLE_FIELDS.version_id,
      header: 'Version ID',
      cell: { type: 'editable' },
      width: 190,
    },
    {
      key: LOAD_TABLE_FIELDS.box_office_multiple,
      header: 'Box Office Multiple',
      cell: { type: 'readonly' },
      width: 140,
    },
    {
      key: LOAD_TABLE_FIELDS.resulting_gross,
      header: selectedModel?.is_domestic
        ? 'US Box Office Gross'
        : 'WW Box Office Gross',
      cell: { type: 'readonly' },
      width: 150,
      formatter: (value) => `$${asPrice(value)}`,
    },
    {
      key: LOAD_TABLE_FIELDS.score,
      header: 'Score',
      cell: { type: 'readonly' },
      width: 120,
    },
    {
      key: LOAD_TABLE_FIELDS.title,
      header: 'Title',
      cell: { type: 'readonly' },
      width: 200,
      minWidth: 150,
      maxWidth: 400,
      resizable: true,
    },
    {
      key: LOAD_TABLE_FIELDS.release_date,
      header: 'Release Date',
      cell: { type: 'readonly' },
      width: 185,
    },
    {
      key: LOAD_TABLE_FIELDS.budget,
      header: 'Production Budget',
      cell: { type: 'readonly' },
      width: 145,
      formatter: (value) => `$${asPrice(value)}`,
    },
    {
      key: LOAD_TABLE_FIELDS.actors,
      header: 'Actors',
      cell: {
        type: 'custom',
        component: ActorMultiSelect,
        props: { disabled: true },
      },
      width: 400,
      minWidth: 200,
      maxWidth: 400,
      resizable: true,
    },
    {
      key: LOAD_TABLE_FIELDS.directors,
      header: 'Directors',
      cell: {
        type: 'custom',
        component: DirectorMultiSelect,
        props: { disabled: true },
      },
      width: 220,
    },
    {
      key: LOAD_TABLE_FIELDS.writers,
      header: 'Writers',
      cell: {
        type: 'custom',
        component: WriterMultiSelect,
        props: { disabled: true },
      },
      width: 220,
    },
    {
      key: LOAD_TABLE_FIELDS.producers,
      header: 'Producers',
      cell: {
        type: 'custom',
        component: ProducerMultiSelect,
        props: { disabled: true },
      },
      width: 220,
    },
    {
      key: LOAD_TABLE_FIELDS.stars,
      header: 'Stars',
      cell: {
        type: 'custom',
        component: StarMultiSelect,
        props: { disabled: true },
      },
      width: 220,
    },
    {
      key: LOAD_TABLE_FIELDS.plot,
      header: 'Plot',
      cell: { type: 'readonly' },
      width: 250,
      minWidth: 200,
      maxWidth: 500,
      resizable: true,
    },
    {
      key: LOAD_TABLE_FIELDS.mpaa_rating,
      header: 'MPAA Rating',
      cell: { type: 'readonly' },
      width: 125,
    },
    {
      key: LOAD_TABLE_FIELDS.runtime,
      header: 'Runtime',
      cell: { type: 'readonly' },
      width: 100,
    },
    {
      key: LOAD_TABLE_FIELDS.aspect_ratio,
      header: 'Aspect Ratio',
      cell: { type: 'readonly' },
      width: 140,
      formatter: (value) => (value && value !== '' ? `${value}:1` : ''),
    },
    {
      key: LOAD_TABLE_FIELDS.genres,
      header: 'Genres',
      cell: {
        type: 'custom',
        component: (props) => <GenreMultiSelect {...props} disabled={true} />,
      },
      width: 220,
      minWidth: 180,
      maxWidth: 350,
      resizable: true,
    },
  ];

  // Load and display stored prediction response
  useEffect(() => {
    if (!predictionResult) return;

    try {
      if (predictionResult.movies) {
        const predictionData = predictionResult.movies.map((movie: any) => ({
          [LOAD_TABLE_FIELDS.internal_id]: movie.internal_id || '',
          [LOAD_TABLE_FIELDS.version_id]: movie.version_id || '',
          [LOAD_TABLE_FIELDS.pred_roi]: movie.pred_roi || '',
          [LOAD_TABLE_FIELDS.box_office_multiple]:
            movie.box_office_multiple || '',
          [LOAD_TABLE_FIELDS.resulting_gross]: movie.resulting_gross || '',
          [LOAD_TABLE_FIELDS.score]: movie.score || '',
          [LOAD_TABLE_FIELDS.title]: movie.title || '',
          [LOAD_TABLE_FIELDS.release_date]: movie.release_date || '',
          [LOAD_TABLE_FIELDS.budget]: movie.budget || '',
          [LOAD_TABLE_FIELDS.actors]: movie.actors || [],
          [LOAD_TABLE_FIELDS.directors]: movie.directors || [],
          [LOAD_TABLE_FIELDS.writers]: movie.writers || [],
          [LOAD_TABLE_FIELDS.producers]: movie.producers || [],
          [LOAD_TABLE_FIELDS.stars]: movie.stars || [],
          [LOAD_TABLE_FIELDS.plot]: movie.plot || '',
          [LOAD_TABLE_FIELDS.mpaa_rating]: movie.mpaa_rating || '',
          [LOAD_TABLE_FIELDS.runtime]: movie.runtime || '',
          [LOAD_TABLE_FIELDS.aspect_ratio]: movie.aspect_ratio || '',
          [LOAD_TABLE_FIELDS.genres]: movie.genres || [],
          [LOAD_TABLE_FIELDS.is_save_allowed]: movie.is_save_allowed,
          is_export_allowed: movie.is_export_allowed,
        }));

        setTableData(predictionData);
      }
    } catch (parseError) {
      console.error('Error processing stored response:', parseError);
    }
  }, [predictionResult]);

  useEffect(() => {
    return () => {
      clearSelection();
    };
  }, [clearSelection]);

  const selectedRow = (location.state as any)?.selectedRow;
  const [tableData, setTableData] = useState<TableRow[]>([
    selectedRow || {
      [LOAD_TABLE_FIELDS.internal_id]: '',
      [LOAD_TABLE_FIELDS.version_id]: '',
      [LOAD_TABLE_FIELDS.pred_roi]: '',
      [LOAD_TABLE_FIELDS.score]: '',
      [LOAD_TABLE_FIELDS.title]: '',
      [LOAD_TABLE_FIELDS.release_date]: '',
      [LOAD_TABLE_FIELDS.budget]: '',
      [LOAD_TABLE_FIELDS.actors]: [],
      [LOAD_TABLE_FIELDS.directors]: [],
      [LOAD_TABLE_FIELDS.writers]: [],
      [LOAD_TABLE_FIELDS.producers]: [],
      [LOAD_TABLE_FIELDS.stars]: [],
      [LOAD_TABLE_FIELDS.plot]: '',
      [LOAD_TABLE_FIELDS.mpaa_rating]: '',
      [LOAD_TABLE_FIELDS.runtime]: '',
      [LOAD_TABLE_FIELDS.aspect_ratio]: '',
      [LOAD_TABLE_FIELDS.genres]: [],
      [LOAD_TABLE_FIELDS.is_save_allowed]: undefined,
      is_export_allowed: undefined,
    },
  ]);

  // Get selectable movie indices (only movies where is_save_allowed is not false)
  const selectableMovieIndices = tableData
    .map((row, index) => ({
      index: index.toString(),
      isSelectable: row[LOAD_TABLE_FIELDS.is_save_allowed] !== false,
    }))
    .filter((item) => item.isSelectable)
    .map((item) => item.index);

  const allSelectableSelected =
    selectableMovieIndices.length > 0 &&
    selectableMovieIndices.every((id) => selectedMovieIds.has(id));

  const someSelected =
    selectableMovieIndices.some((id) => selectedMovieIds.has(id)) &&
    !allSelectableSelected;

  const handleSelectAll = () => {
    if (allSelectableSelected) {
      clearSelection();
    } else {
      selectAllMovies(selectableMovieIndices);
    }
  };

  const handleCellChange = (
    rowIndex: number,
    column: string,
    value: string | any[],
  ): void => {
    const newData = [...tableData];
    newData[rowIndex][column as keyof TableRow] = value;
    setTableData(newData);
  };

  const addNewRow = (): void => {
    const newRow: TableRow = {
      [LOAD_TABLE_FIELDS.internal_id]: '',
      [LOAD_TABLE_FIELDS.version_id]: '',
      [LOAD_TABLE_FIELDS.pred_roi]: '',
      [LOAD_TABLE_FIELDS.score]: '',
      [LOAD_TABLE_FIELDS.title]: '',
      [LOAD_TABLE_FIELDS.release_date]: '',
      [LOAD_TABLE_FIELDS.budget]: '',
      [LOAD_TABLE_FIELDS.actors]: [],
      [LOAD_TABLE_FIELDS.directors]: [],
      [LOAD_TABLE_FIELDS.writers]: [],
      [LOAD_TABLE_FIELDS.producers]: [],
      [LOAD_TABLE_FIELDS.stars]: [],
      [LOAD_TABLE_FIELDS.plot]: '',
      [LOAD_TABLE_FIELDS.mpaa_rating]: '',
      [LOAD_TABLE_FIELDS.runtime]: '',
      [LOAD_TABLE_FIELDS.aspect_ratio]: '',
      [LOAD_TABLE_FIELDS.genres]: [],
      [LOAD_TABLE_FIELDS.is_save_allowed]: undefined,
      is_export_allowed: undefined,
    };
    setTableData([...tableData, newRow]);
  };

  const handleSaveSelected = async (): Promise<void> => {
    const selectedIndices = Array.from(selectedMovieIds);

    if (selectedIndices.length === 0) return;

    setIsSaving(true);

    try {
      const selectedMovies =
        predictionResult?.movies?.filter((movie, index) =>
          selectedIndices.includes(index.toString()),
        ) || [];

      const modelId = predictionResult?.model_id || '';

      if (!modelId) {
        toast.error(
          'No model ID available for saving. Please run predictions first.',
        );
        return;
      }

      // Merge original prediction data with edited values from tableData
      const moviesToSave = selectedMovies.map((movie, movieIndex) => {
        const originalIndex =
          predictionResult?.movies?.findIndex((m) => m === movie) ?? -1;
        const editedData = tableData[originalIndex];

        return {
          ...movie,
          version_id:
            editedData?.[LOAD_TABLE_FIELDS.version_id] || movie.version_id,
          plot: movie.plot?.trim() || null,
          is_save_allowed: undefined,
        };
      });

      await predictionEngineSave({
        movies: moviesToSave as any,
        model_id: modelId,
      });

      toast.success(
        `Successfully saved ${selectedIndices.length} movie${selectedIndices.length === 1 ? '' : 's'}!`,
      );
      clearSelection();
    } catch (error) {
      toast.error('Failed to save selected movies. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGoToGrouped = (): void => {
    history.push(`${routes.PREDICTIONS_ENGINE}/grouped`);
  };

  const handleGoToDetailed = (): void => {
    history.push(`${routes.PREDICTIONS_ENGINE}/detailed`);
  };

  const selectedCount = getSelectedCount();
  const isExportAllowed = tableData.some(
    (row) => row.is_export_allowed === true,
  );

  const selectedTableData = tableData.filter((_, index) =>
    selectedMovieIds.has(index.toString()),
  );

  const actionButtons = [
    <Button
      variant="default"
      onClick={handleSaveSelected}
      disabled={selectedCount === 0 || isSaving || !hasSavePredictions}
    >
      {isSaving ? 'Saving...' : `Save Selected (${selectedCount})`}
    </Button>,

    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="default"
          disabled={selectedCount === 0 || !isExportAllowed || isExporting}
        >
          Export ({selectedCount})
          <ChevronDownIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem
          onClick={() =>
            handleExport('CSV', selectedTableData, columns, setIsExporting)
          }
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export as CSV'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>,

    <Button variant="outline" onClick={handleGoToGrouped}>
      Go to Grouped
    </Button>,

    ...(isSuperbUser && hasCanSeeDetailedPredictions
      ? [
          <Button variant="outline" onClick={handleGoToDetailed}>
            Go to Detailed
          </Button>,
        ]
      : []),
  ];

  if (isRunningPending) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-py-24 tw-text-center">
        <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
        <p className="tw-text-sm tw-text-muted-foreground">
          Running predictions...
        </p>
      </div>
    );
  }

  if (pendingError) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-py-24 tw-text-center">
        <p className="tw-text-sm tw-text-destructive">{pendingError}</p>
        <p className="tw-text-xs tw-text-muted-foreground">
          Your data is still on the previous tab.
        </p>
        <Button variant="outline" onClick={() => window.close()}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <PredictionsTable
      actionButtons={actionButtons}
      columns={columns}
      tableData={tableData}
      onCellChange={handleCellChange}
      onAddRow={addNewRow}
      disableAddButton={true}
      disableModelSelection={true}
    />
  );
};

export default PredictRoute;
